import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Heart } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PulsePostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const fallbackImages = ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/news_update_one.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/news_update_two.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/news_update_three.png"];
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className={`flex flex-col gap-5 ${i === 1 ? "md:mt-6" : ""}`}>
            <div className="aspect-[4/5] w-full rounded-lg bg-muted/40 animate-pulse" />
            <div className="h-4 w-32 rounded-md bg-muted/40 animate-pulse" />
            <div className="h-6 w-full rounded-md bg-muted/40 animate-pulse" />
            <div className="h-4 w-3/4 rounded-md bg-muted/40 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <p className="font-default text-sm text-muted-foreground">
        Gagal memuat berita saat ini.
      </p>;
  }
  if (!posts || posts.length === 0) {
    return <p className="font-default text-sm text-muted-foreground">
        Belum ada berita tersedia. Silakan kembali lagi nanti.
      </p>;
  }
  const displayPosts = posts.slice(0, 3);
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-8 lg:gap-10">
      {displayPosts.map((post, i) => {
      const categoryName = post.categories?.[0]?.name || "Berita";
      const dateString = post.date ? new Date(post.date).toLocaleDateString("id-ID", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) : "";
      const authorName = post.author?.name || "Redaksi PP";
      const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";
      const excerpt = (post.excerpt || "").replace(/<[^>]+>/g, "").trim();
      const featuredImg = post.featuredImage || fallbackImages[i];
      return <article data-index={i} key={post.id} className={`group flex flex-col gap-5 transition-transform duration-300 ease-out hover:-translate-y-1 ${i === 1 ? "md:mt-6" : ""}`}>
            <Link to={post.link || "#"} className="block overflow-hidden rounded-lg">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg bg-muted">
                <img src={featuredImg} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
              </div>
            </Link>

            <div className="flex items-center gap-3">
              <span className="inline-flex items-center rounded-md border px-3 py-1 text-[11px] font-medium uppercase tracking-wider" style={{
            borderColor: "var(--primary)",
            color: "var(--primary)"
          }}>
                {categoryName}
              </span>
              <span className="h-1 w-1 rounded-lg" style={{
            backgroundColor: "var(--muted-foreground)"
          }} aria-hidden="true" />
              <span className="font-default text-xs text-muted-foreground">
                {dateString}
              </span>
            </div>

            <Link to={post.link || "#"} className="block">
              <h3 className="font-default text-2xl font-semibold leading-snug text-foreground transition-colors duration-300 group-hover:text-primary line-clamp-3">
                {post.title}
              </h3>
              <span className="mt-2 block h-px w-0 bg-primary transition-all duration-300 ease-out group-hover:w-16" />
            </Link>

            {excerpt && <p className="font-default text-sm leading-relaxed text-muted-foreground line-clamp-2">
                {excerpt}
              </p>}

            <div className="mt-auto flex items-center gap-3 pt-2">
              <SafeHydrate><Avatar className="size-7">
                {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
                <AvatarFallback className="bg-muted text-[10px] font-default text-muted-foreground">
                  {authorName.split(" ").map(n => n[0]).slice(0, 2).join("")}
                </AvatarFallback>
              </Avatar></SafeHydrate>
              <span className="font-default text-[13px] text-foreground">
                {authorName}
              </span>
              <span className="font-default text-[13px] text-muted-foreground">
                · 6 menit baca
              </span>
            </div>
          </article>;
    })}
    </div>;
}
export default function Pagination() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="5096" id="pagination" className="relative w-full bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="flex flex-col gap-10 md:flex-row md:items-end md:justify-between md:gap-8">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-lg" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
              <span className="font-default text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Berita Terkini
              </span>
            </div>
            <h2 className="font-default text-4xl font-bold leading-[1.05] text-foreground md:text-5xl lg:text-[56px]">
              Informasi Terbaru
              <br />
              <span className="text-primary">dari organisasi kami.</span>
            </h2>
          </div>

          <div className="md:pb-3">
            <Link to="/NewsPage" className="group inline-flex items-center gap-2 font-default text-sm font-medium text-foreground transition-colors hover:text-primary">
              <Heart className="size-4" style={{
              color: "var(--primary)"
            }} />
              <span>Lihat semua berita</span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="mt-14 md:mt-20">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date"
        }}>
            <PulsePostsConsumer />
          </PostsProvider>
        </div>

        <div className="mt-16 flex justify-center md:hidden">
          <Link to="/NewsPage">
            <Button variant="outline" className="rounded-lg">
              Lihat semua berita
              <ArrowRight className="size-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}